import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

/**
 * 费用汇总数据
 */
export interface ExpenseSummary {
  totalExpense: number;
  myExpense: number;
  myDebt: number;
  currency?: string;
}

/**
 * ExpenseManagement 组件属性
 */
export interface ExpenseManagementProps {
  /**
   * 费用汇总数据
   */
  summary?: ExpenseSummary;
  /**
   * 是否可以添加费用
   */
  canAddExpense?: boolean;
  /**
   * 添加费用回调
   */
  onAddExpense?: () => void;
  /**
   * 查看所有费用回调
   */
  onViewExpenses?: () => void;
  /**
   * 翻译函数
   */
  t?: (key: string, params?: Record<string, any>) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'travelDetail.content.expenseManagement': '费用管理',
    'travelDetail.content.addExpense': '添加费用',
    'travelDetail.content.totalExpense': '总支出',
    'travelDetail.content.myExpense': '我的支出',
    'travelDetail.content.myDebt': '我的欠款',
    'travelDetail.content.noExpenses': '暂无费用记录',
    'travelDetail.content.noExpensesSubtext': '完整功能请使用移动端应用',
    'travelDetail.content.viewAllExpenses': '查看所有费用',
  };
  return translations[key] || key;
};

/**
 * ExpenseManagement 组件
 * 
 * 用于显示和管理旅行费用的共享组件
 */
const ExpenseManagement: React.FC<ExpenseManagementProps> = ({
  summary = { totalExpense: 0, myExpense: 0, myDebt: 0, currency: '¥' },
  canAddExpense = true,
  onAddExpense,
  onViewExpenses,
  t = defaultT,
  style,
}) => {
  const handleAddExpense = () => {
    if (onAddExpense) {
      onAddExpense();
    }
  };

  const handleViewExpenses = () => {
    if (onViewExpenses) {
      onViewExpenses();
    }
  };

  const currencySymbol = summary.currency || '¥';

  return (
    <View style={[styles.container, style]}>
      <View style={styles.header}>
        <Text style={styles.title}>💰 {t('travelDetail.content.expenseManagement')}</Text>
        {canAddExpense && onAddExpense && (
          <TouchableOpacity style={styles.addButton} onPress={handleAddExpense}>
            <Icon name="add" size={16} color={MODULE_COLORS.travel} />
            <Text style={styles.addButtonText}>{t('travelDetail.content.addExpense')}</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.summary}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t('travelDetail.content.totalExpense')}</Text>
          <Text style={styles.summaryValue}>
            {currencySymbol} {summary.totalExpense.toFixed(2)}
          </Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t('travelDetail.content.myExpense')}</Text>
          <Text style={styles.summaryValue}>
            {currencySymbol} {summary.myExpense.toFixed(2)}
          </Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryLabel}>{t('travelDetail.content.myDebt')}</Text>
          <Text style={[styles.summaryValue, styles.debtValue]}>
            {currencySymbol} {summary.myDebt.toFixed(2)}
          </Text>
        </View>
      </View>

      <View style={styles.emptyState}>
        <Icon name="receipt" size={48} color="#ccc" />
        <Text style={styles.emptyText}>{t('travelDetail.content.noExpenses')}</Text>
        <Text style={styles.emptySubtext}>{t('travelDetail.content.noExpensesSubtext')}</Text>
        {onViewExpenses && (
          <TouchableOpacity style={styles.viewButton} onPress={handleViewExpenses}>
            <Text style={styles.viewButtonText}>{t('travelDetail.content.viewAllExpenses')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.8)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
  },
  addButtonText: {
    marginLeft: 4,
    fontSize: 12,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
  },
  summary: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    justifyContent: 'space-between',
  },
  summaryItem: {
    alignItems: 'center',
    flex: 1,
  },
  summaryLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  debtValue: {
    color: '#EF4444',
  },
  emptyState: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#6B7280',
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 4,
    marginBottom: 16,
    textAlign: 'center',
  },
  viewButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: MODULE_COLORS.travel,
    borderRadius: 6,
  },
  viewButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default ExpenseManagement;
